import type { AnswerUserQuestion, FeedbackAnswerQuestion } from '@repo/types/questions/Question';

import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { AlertCircleIcon, ArrowLeftIcon, CheckCircleIcon, CircleSlash } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { SectionMarked } from '@/components/features/quiz/question/question-marked';
import QuizAnswerResultSplit from '@/components/features/quiz/results/QuizAnswerResultSplit';
import QuizQuestionResultCard from '@/components/features/quiz/results/QuizQuestionResultCard';
import QuizBasicStats from '@/components/features/quizzes/QuizBasicStats';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { ScrollProvider } from '@/components/ui/scroll';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle';
import { useManageQuizMark } from '@/context/quiz-marked';
import { useQuizResultStats } from '@/hooks/quiz';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

export default function ManageQuizMarkPage() {
  const navigate = useNavigate();

  const {
    quizMarkedQuery: { data, isPending, isError, errorMessage },
    editQuizMarked,
    markedData,
    updateMarkedQuestion,
    removeMarkedData,
  } = useManageQuizMark();

  const [onlyMarked, setOnlyMarked] = useState<string>('off');

  const quiz = data?.quiz;
  const attempt = data?.attempt;
  const sections = data?.quiz.content ?? [];

  const resultData = useQuizResultStats({
    quiz: data?.quiz,
    attempt: data?.attempt,
    marked: markedData ?? data?.attempt.body.marked,
    selectedIdx: null,
  });
  const onSubmit = async () => {
    if (!markedData || !data) return;
    await editQuizMarked
      .mutateAsync({
        marked: markedData,
        id: data.quiz.id,
        attemptId: data.attempt.id,
      })
      .then(() => {
        removeMarkedData();
        navigate(-1);
      })
      .catch((error) => {
        toasterMessageHandler(quizErrorMessages, error);
      });
  };

  const onEditQuestionMark = (data: Partial<FeedbackAnswerQuestion>, sectionIdx: number, questionIdx: number) => {
    updateMarkedQuestion(data, sectionIdx, questionIdx);
  };

  if (isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className={'z-0'} />
          <FeaturedIcon size={'lg'} className={'z-10'}>
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
          <EmptyStateFooter>
            <Button variant="secondary" className="w-full max-w-sm" onClick={() => navigate(-1)}>
              <ArrowLeftIcon className="transition-transform group-hover:-translate-x-0.5" />
              Powrót
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="base-direction-col">
        {/* Header skeleton */}
        <div className="w-full flex-col gap-2 rounded-2xl border p-4">
          <Skeleton className="h-5 w-12 rounded-sm" />
          <Skeleton className="my-2 h-4 w-24 rounded-sm" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
          </div>
        </div>
        <div className="flex h-full w-full flex-col gap-4 lg:flex-row">
          {/* Results panel skeleton */}
          <div className="w-full rounded-2xl border lg:w-xs lg:min-w-xs">
            <div className="w-full border-b p-4">
              <Skeleton className="h-6 w-16" />
            </div>
            <div className="flex flex-row flex-wrap gap-2 p-3 text-nowrap md:p-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-16 rounded-sm" />
              ))}
            </div>
          </div>
          {/* Content skeleton */}
          <div className="w-full space-y-4">
            {Array.from({ length: 2 }).map((_, sectionIdx) => (
              <div key={sectionIdx} className="rounded-2xl border p-4">
                <Skeleton className="mb-4 h-6 w-1/3" />
                {Array.from({ length: 3 }).map((_, questionIdx) => (
                  <div key={questionIdx} className="mb-6">
                    <Skeleton className="mb-2 h-4 w-1/2" />
                    <Skeleton className="mb-2 h-10 w-full" />
                    <div className="flex gap-2">
                      <Skeleton className="h-8 w-20" />
                      <Skeleton className="h-8 w-20" />
                      <Skeleton className="h-8 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ScrollProvider options={{ scrollIntoViewOptions: { behavior: 'smooth' } }}>
      {/* dim + spinner overlay */}
      <LoadingOverlay isLoading={editQuizMarked.isPending}>
        <LoadingOverlaySpinner />
        <LoadingOverlayLabel>Zapisywanie...</LoadingOverlayLabel>
      </LoadingOverlay>
      <div className="base-direction-col">
        <div className="bg-background flex w-full flex-col gap-4 rounded-2xl border p-4 md:gap-3">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeftIcon />
            </Button>
            {markedData ? (
              <Button onClick={onSubmit} disabled={!markedData}>
                Zapisz <CheckCircleIcon />
              </Button>
            ) : (
              <Badge variant={'destructive'}>
                <AlertCircleIcon />
                Brak udzielonych odpowiedzi
              </Badge>
            )}
          </div>
          <h5 className="text-center text-xl font-bold md:text-start">{quiz?.name}</h5>
          <div className="flex flex-wrap items-center justify-between gap-6">
            {quiz && <QuizBasicStats quiz={quiz} className="w-full md:flex md:flex-row md:flex-wrap md:[&>div]:items-start" />}
            <ToggleGroup type="single" groupVariant={'default'} variant={'outline'} value={onlyMarked} onValueChange={setOnlyMarked} className="w-full md:w-fit">
              <ToggleGroupItem value={'off'} className="md:min-w-44">
                Wymagane oceny
              </ToggleGroupItem>
              <ToggleGroupItem value={'on'} className="md:min-w-44">
                Wszystkie
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
        <div className="flex w-full grow flex-col gap-4 lg:flex-row">
          <div className="bg-background relative top-0 z-10 h-fit w-full grow gap-4 rounded-2xl border lg:sticky lg:top-4 lg:w-xs lg:min-w-xs">
            <div className="border-b p-4">
              <h5 className="text-base font-medium">Wynik</h5>
            </div>

            <div className="flex flex-col gap-6 p-4">
              <div className="flex flex-row flex-wrap gap-2 text-nowrap">
                {sections.map((section, sectionIndex) => {
                  const sectionMarked = markedData ?? attempt?.body.marked;
                  const attemptAnswers = attempt?.body.answers;
                  return section.questions.map((question, questionIndex) => (
                    <QuizQuestionResultCard
                      key={`${sectionIndex}-${questionIndex}`}
                      idx={questionIndex}
                      sectionIdx={sectionIndex}
                      question={question}
                      answer={(attemptAnswers?.at(sectionIndex)?.at(questionIndex) as AnswerUserQuestion | null) ?? null}
                      marked={sectionMarked?.at(sectionIndex)?.at(questionIndex) as FeedbackAnswerQuestion}
                    />
                  ));
                })}
              </div>
              <QuizAnswerResultSplit
                correct={resultData.correct}
                incorrect={resultData.incorrect}
                partially={resultData.partially}
                unmarked={resultData.unmarked}
                unanswered={resultData.unanswered}
                questionCnt={data.quiz.content.reduce((sum, section) => sum + section.questions.length, 0) || 0}
              />
            </div>
          </div>
          <div className="w-full space-y-4">
            {quiz &&
              attempt &&
              quiz.content.map((section, idx) => {
                return (
                  <SectionMarked
                    key={idx}
                    idx={idx}
                    marked={markedData ?? attempt.body.marked}
                    answers={attempt.body.answers}
                    section={section}
                    onEditMark={onEditQuestionMark}
                    showOnlyNotMarked={onlyMarked === 'off'}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </ScrollProvider>
  );
}
